import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { backendServices, portalUserService } from '../services';

const DefaultUserGroupKeys: Array<keyof backendServices.ViewModels.UserGroupViewModel> = [
    'Name',
    'Id',
    'CompanyId',
    'Name',
];

/**
 * We can't expose the full list of properties
 * See: https://github.com/TanStack/query/discussions/3227
 */
type UserGroupsQueryOptions = Pick<UseQueryOptions, 'enabled'>;

// TODO: fix the type inference
/**
 * Custom hook to fetch user groups based on the provided parameters.
 *
 * @param params - The parameters for fetching user groups.
 * @param params.companyId - The ID of the company to fetch user groups for. Defaults to the current company ID.
 * @param params.select - An array of keys to select specific properties from the UserGroupViewModel.
 * @param params.queryOptions - Additional query options for the useQuery hook.
 * @param params.childCompanyId - Optional. The ID of the child company, or a comma-separated list of child company IDs.
 * @param params.omitSelect - Optional. Flag used to remove the `$select` param from the request. This will make the API return the full ViewModel (including computed properties).
 *
 * @returns - An object containing the fetched user groups, loading state, and error.
 */
export function useUserGroups<S extends Array<keyof backendServices.ViewModels.UserGroupViewModel>>({
    companyId,
    select,
    queryOptions,
    childCompanyId,
    omitSelect = false,
}: {
    companyId?: number;
    select?: S;
    queryOptions?: UserGroupsQueryOptions;
    childCompanyId?: string;
    omitSelect?: boolean;
} = {}) {
    const targetCompanyId = companyId ?? portalUserService.getCurrentCompanyId();

    const userGroupProps = omitSelect ? undefined : (select ?? DefaultUserGroupKeys).join(',');

    const {
        data: userGroups = [],
        isLoading,
        isFetching,
        error,
    } = useQuery(
        ['CompanyUsers', targetCompanyId, userGroupProps],
        async () => {
            const userGroupApi = new backendServices.Apis.UserGroupApi();

            const res = await userGroupApi.getUserGroups({
                companyId: targetCompanyId,
                $select: userGroupProps,
                childCompanyId,
            });

            if (omitSelect) {
                return res.data as backendServices.ViewModels.UserGroupViewModel[];
            }

            if (select != null) {
                return res.data as Pick<backendServices.ViewModels.UserGroupViewModel, typeof select[number]>[];
            }

            return res.data as Pick<
                backendServices.ViewModels.UserGroupViewModel,
                typeof DefaultUserGroupKeys[number]
            >[];
        },
        {
            staleTime: Infinity,
            ...queryOptions,
        }
    );

    return {
        userGroups,
        isLoading: isLoading || isFetching,
        error,
    };
}
